import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PLAN_URL from "../../../utils/PLAN_URL";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import CreateMaterialModal from "./CreateMeterialModal";
import { localizationTextObjectForInformation } from "../../../utils/constants";
import { COLORS } from "../../APS/Learning/constants/config";
import { useTableInstance } from "./useTabeInstance";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;
const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;
const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;
const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;
const ToolbarCenter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;
const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;

  /* 버튼 사이 간격 조정 */
  & > div {
    display: flex;
    gap: 0.5rem;
  }
`;
const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* 테이블 스타일 오버라이드 */
  .MuiPaper-root {
    box-shadow: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .MuiTable-root {
    border-collapse: separate;
    border-spacing: 0;
  }

  .MuiTableContainer-root {
    flex: 1;
    overflow: auto;
  }

  .MuiTableHead-root {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--mui-palette-background-paper);
  }

  .MuiTableRow-root:hover {
    background-color: var(--mui-palette-action-hover);
  }
`;

const createUrl = `${PLAN_URL}/api/material/create`;
const deleteUrl = `${PLAN_URL}/api/material/delete`;
const bringUrl = `${PLAN_URL}/api/material/bring`;

function Material() {
  const { group_code, project_code } = useParams();
  const [data, setData] = useState([]);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingValues, setCreatingValues] = useState({
    material_name: "",
    material_description: "",
  });
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });
  const columns = [
    {
      accessorKey: "material_id",
      header: "ID",
      enableEditing: false,
    },
    {
      accessorKey: "material_name",
      header: "Material Name",
      //   size: 200,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "default_unit",
      header: "기본 단위",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "material_description",
      header: "Description",
      ///   size: 200,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          bringUrl + `?group_code=${group_code}&project_code=${project_code}`
        );
        const items = res.data;
        setData(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error(err);
        openSnackbar("자료를 불러오지 못했습니다.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [updateStatus]);
  const handleDelete = async (selectedRowIds) => {
    try {
      await axios.delete(deleteUrl, {
        data: { ids: selectedRowIds, group_code, project_code },
      });
      setRowSelection({});
      openSnackbar("선택한 항목을 삭제했습니다.", "success");
    } catch (err) {
      console.error(err);
      openSnackbar("삭제에 실패했습니다.", "error");
    } finally {
      setTableLoading(false);
      setUpdateStatus((prev) => !prev);
    }
  };
  const { table, ActionToolbar } = useTableInstance({
    columns,
    data,
    openSnackbar,
    setUpdateStatus,
    handleDelete,
  });
  return (
    <>
      <Wrapper>
        <FrameContainer>
          <ToolbarContainer>
            <ToolbarLeft>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ fontSize: "22px !important", color: "#111" }}
                >
                  자재 관리
                </Typography>
                {/* <Tooltip
                title="이동"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  goToHelpLink(
                    "https://help.ese-tech.com/ko/scheduler#h-3-ai%EC%83%9D%EC%82%B0%EA%B3%84%ED%9A%8D-%ED%8E%98%EC%9D%B4%EC%A7%80"
                  )
                }
              >
                <HelpOutlineIcon />
              </Tooltip> */}
              </div>
            </ToolbarLeft>
            <ToolbarRight>
              <ActionToolbar table={table} />
            </ToolbarRight>
          </ToolbarContainer>
          <InnerContainer>
            <MaterialReactTable table={table} />
          </InnerContainer>
        </FrameContainer>
      </Wrapper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Material;
